import config from '@/config'
import React, { useCallback, useEffect, useState } from 'react'
import { NavigateFunction, Params, useLocation, useNavigate, useParams, useSearchParams } from 'react-router'
import { makeStyles } from '@mui/styles'
import setDocumentTitle from './setDocumentTitle'
import Loader from '@/modules/base/components/loader/loader'
// none of them is read by the HOC - a page rendered directly (not as a route Component) passes none
interface IHoCProps {
  menuItem?: string
  title?: string
  submenuItem?: string,
  isLoading?: boolean,
}

const useStyles = makeStyles(() => ({
  loader: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'fixed',
    top: 0,
    bottom: 0,
    right: 0,
    left: 0,
    background: 'rgba(0, 0, 0, 0.3)',
  },
}))

export const withAsyncData = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
  title?: (() => string) | string,
  fetchData?: (options: {searchParams?: URLSearchParams, params: Params, navigate: NavigateFunction}) => Promise<{overwrittenTitle?: string; [key: string]: unknown}>,
) => {
  const HOC: React.FC<P & IHoCProps> = ({
    ...props
  }) => {
    const navigate = useNavigate()
    const params = useParams()
    const location = useLocation()
    const [searchParams] = useSearchParams()
    const classes = useStyles()

    const [overwrittenTitle, setOverwrittenTitle] = useState<string | null>(null)
    const [data, setData] = useState<object | null>(null)
    const [isLoading, setIsLoading] = useState<boolean>(!!fetchData)
    // FLAG:useDocuAi — `reloadData()` for the wrapped page: run `fetchData` again, exactly like the
    // first load (loader up, page remounted with the fresh result). Additive — nothing calls it
    // unless a page asks for it. First user: the expense/revenue detail after a DocuAI re-analysis
    // (plan omxweb-draft-receipt-instead-of-review-row T14), whose record changes state and values
    // on the server while the page was loaded exactly once.
    const [reloadSequence, setReloadSequence] = useState(0)
    const reloadData = useCallback(() => setReloadSequence(sequence => sequence + 1), [])

    if (!overwrittenTitle) {
      if (title) {
        const titleText = typeof title === 'function' ? title() : title
        setDocumentTitle(titleText)
      }
    } else {
      setDocumentTitle(overwrittenTitle, true)
    }

    useEffect(() => {
      if (!fetchData) {
        if (location.state?.refreshData) {
          setData({})
        }
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      fetchData({ navigate, params, searchParams }).then((data) => {
        const { overwrittenTitle, ...restData } = data || {}
        if (overwrittenTitle) {
          setOverwrittenTitle(`${overwrittenTitle} - ${config.documentTitleSuffix}`)
        }
        setData(restData)
      }).catch((error) => {
        console.error(error)
      }).finally(() => setIsLoading(false))
    }, [fetchData, location.state?.refreshData, reloadSequence])

    if (isLoading) {
      return (
        <div className={classes.loader}>
          <Loader />
        </div>
      )
    }
    const wrappedProps = { ...(props as P), ...data, isLoading, reloadData } as P
    return <WrappedComponent {...wrappedProps} />
  }

  return HOC
}
